/**
 * @(#)2020/03/10.
 * BaseUpload - 基础部分，上传组件的基础部分，公共部分
 * @author 足道
 * Copyright (c) 2019, YUNXI. All rights reserved.
 * YUNXI PROPRIETARY/CONFIDENTIAL. Use is subject to license terms.
 */

import React from "react";
import Basics from "../../Basics";
import { Upload, Spin } from "antd";
import { EventEmitter } from "events";
import { isEqual, cloneDeep, isArray } from "lodash";
import DelPreview from "../Components/DelPreview";
import { defaulProps, defaulState } from "../param";
import { accept } from "../../../../utils/fileInput";
import UploadPreview from "../Components/UploadPreview";
import renderUploadBtn from "../Components/UploadButton";
import { Connection, YxComponent } from "../../../YxComponent";
import {
  get,
  batchGet,
  nodeFactory,
  toArray,
  random,
} from "../../../../utils/index";
import {
  listTyleEmum,
  staticFileList,
  uploadRules,
  dragControl,
  setFieldValue,
  videoControl,
} from "./tools";

@Connection(defaulProps)
class BaseUpload extends YxComponent {
  constructor(props, context) {
    super(props, context);
    this.state = defaulState(this.props);
    this.uploadAntd = React.createRef();
    this.eventEmitter = new EventEmitter();
    this.fileNumber = 0;
    this.id = random();
  }

  /**
   * 从子类传入的参数
   *
   * @readonly
   * @memberof BaseUpload
   */
  get extendParam() {
    const setState = (...args) => this.setState(...args);
    const getSign = get(this.props, "getSign", () => {});
    return {
      uploadUrl: this.props.uploadUrl,
      getSign: () => getSign(setState),
      uploadFile: this.props.uploadFile,
      extraUploadParam: this.props.extraUploadParam,
      getValueFromHandle: this.props.getValueFromHandle,
      beforeUploadExtraAction: this.props.beforeUploadExtraAction,
    };
  }

  /**
   * 当props变化，节点渲染完成触发
   * 当 form不为空时意味在表单内部，手动更新自身
   * @param {object} nextProps
   * @param {object} nextState
   * @returns
   * @memberof BaseUpload
   */
  componentWillReceiveProps(nextProps) {
    const [list1, list2] = [this.props.list, nextProps.list];
    if (!isEqual(list1, list2)) {
      setFieldValue(nextProps, this.fileNumberReset);
    }
    if (!isEqual(this.props.isRead, nextProps.isRead) && !nextProps.isRead) {
      this.extendParam.getSign(nextProps);
    }
  }

  componentDidMount = async () => {
    const uploadAntd = get(this, "uploadAntd.current", null);
    this.props.load && this.props.load(uploadAntd);
    await this.extendParam.getSign(this.props);
    setFieldValue(this.props, this.fileNumberReset);
    this.eventEmitter.addListener("dragOver", this.dragOver);
  };

  fileNumberReset = (arr) => {
    this.fileNumber = get(arr, "length", this.fileNumber);
  };

  componentDidUpdate() {
    dragControl(this.id, this.props, this.eventEmitter);
    videoControl(this.props, this.id);
  }

  dragOver = ({ dragIndex, index }) => {
    const list = this.state.fileListByForm;
    [list[dragIndex], list[index]] = [list[index], list[dragIndex]];
    this.setState({ fileListByForm: [...list] });
    const param = [list, this.props.id, null, "succeed", dragIndex, index];
    this.props.form.setFieldsValue({ [this.props.id]: list });
    this.props.change && this.props.change(...param);
    // console.log('拖拽完成', { dragIndex, index }, list)
  };

  /**
   * 当文件上传成功
   *
   * @param {File} file
   * @param {Array<File>} fileList
   * @memberof BaseUpload
   */
  onDone = async (file, fileList) => {
    this.setState({ loading: false }, () => {
      const urls = this.getValueFromEvent({ file, fileList });
      const param = [urls, this.props.id, file, "succeed"];
      this.props.actionCallback && this.props.actionCallback(...param);
      this.props.change && this.props.change(...param);
      console.log(`上传组件完成上传 ，当前文件uid为${file.uid},urls为`, urls);
      this.props.message.done();
    });
  };

  /**
   * 当文件删除完成
   *
   * @param {File} file
   * @param {Array<File>} fileList
   * @memberof BaseUpload
   */
  onRemoved(file, fileList) {
    const copyFileList = [...fileList];
    const urls = this.fileList.map((item) => item.url);
    const param = [urls, this.props.id, file, "removed"];
    this.setState({ loading: false }, () => {
      this.props.actionCallback && this.props.actionCallback(...param);
      this.props.change && this.props.change(...param);
      this.props.message.remove();
    });
    console.log(
      `上传组件完成删除 ，当前文件uid为${file.uid},url为${file.url}`,
      copyFileList
    );
  }

  /**
   * 当上传发生异常
   *
   * @param {File} file
   * @param {Array<File>} fileList
   * @memberof BaseUpload
   */
  onError = (file, fileList) => {
    const errorList = [...this.state.errorList, file];
    fileList = fileList.filter((file_) => file.uid !== file_.uid);
    this.setState({ loading: false, fileList, errorList });
    const error = get(file, "error");
    console.log(
      `上传组件发生异常 ，当前文件uid为${file.uid}`,
      error,
      errorList
    );
    if (error.status === 403) {
      this.extendParam.uploadFile.getSign("error", "重试", file);
    }
    this.props.message.error();
  };

  onRemove = (file) => {
    return DelPreview(this.props, file);
  };

  /**
   * 当文件变化触发
   *
   * @param { File,Array<File>} { file, fileList }
   * @memberof BaseUpload
   */
  onChange = async ({ file, fileList }) => {
    const { status } = file;
    this.setState({ fileList: fileList, loading: true });
    if (status === "done") {
      await this.onDone(file, fileList);
    } else {
      if (status === "error") {
        this.onError(file, fileList);
        this.fileNumber -= 1;
      } else if (status === "removed") {
        this.onRemoved(file, fileList);
        this.fileNumber -= 1;
      }
    }
  };

  /**
   * 上传文件之前触发
   *
   * @param {File} file
   * @returns Promise
   * @memberof BaseUpload
   */
  beforeUpload(file) {
    return new Promise(async (resolve, reject) => {
      this.fileNumber += 1;
      let form = this.extendParam.uploadFile.formParams(file);
      await this.extendParam.beforeUploadExtraAction(file);
      if (!uploadRules(file, this.fileList, this.props, this.fileNumber)) {
        this.fileNumber -= 1;
        reject();
      } else {
        const uploadFormList = this.state.uploadFormList;
        const uploadForm = { form, uid: file.uid };
        uploadFormList.push(uploadForm);
        // console.log('uploadFormListuploadFormList', { urlList: this.imgUrlMap(get(this.props, 'uploadUrl'), uploadFormList), paramList: uploadFormList })
        get(this.props, "beforeUpload", () => {})({
          urlList: this.imgUrlMap(get(this.props, "uploadUrl"), uploadFormList),
          paramList: uploadFormList,
        });
        this.setState({ uploadFormList }, resolve);
      }
    });
  }
  /**
   * description:
   * param : {obj} url 图片host
   * param :{array}  uploadFormList 图片配置参数
   * return {array} 处理后图片地址
   */
  imgUrlMap(url, uploadFormList = []) {
    if (isArray(uploadFormList)) {
      return uploadFormList.map((obj, index) => {
        return (obj.url = `${url}/${get(obj, "form.key")}`);
      });
    }
    return [];
  }

  /**
   * 点击预览
   *
   * @param {File} file
   * @memberof BaseUpload
   */
  onPreview(file) {
    const type = get(this.props, "previewType", "img");
    if (["img", "video", "imgs", "image", "office"].includes(type)) {
      this.setState({
        previewImage: file.thumbUrl || file.url,
        previewVisible: true,
      });
    } else {
      window.open(file.url);
    }
  }

  closePreView = () => {
    this.setState({ previewVisible: false });
  };

  get loading() {
    const fileStatus = this.state.fileList.map(
      (file) => file.status === "uploading"
    );
    const statusList = [this.state.loading, ...fileStatus];
    return statusList.some((ele) => ele === true);
  }

  get fileList() {
    // 只读对象，文件列表
    if (this.loading) {
      let stateList = this.state.fileList;
      return stateList;
    } else {
      const list = staticFileList(
        this.props,
        this.state,
        this.extendParam.uploadFile
      );
      return list;
    }
  }

  get accept() {
    // 文件类型控制，只读
    const type = get(this.props, "type", null);
    return accept(type);
  }

  get previewParam() {
    return {
      type: this.props.type,
      onCancel: this.closePreView,
      url: this.state.previewImage,
      showType: this.props.showType,
      visible: this.state.previewVisible,
      previewType: get(this.props, "previewType", "img"),
    };
  }

  /**
   * 组建上传参数
   *
   * @param {object} file
   * @returns
   * @memberof BaseUpload
   */
  uploadFormData = (file) => {
    let uid = file.uid;
    const result = this.state.uploadFormList.find((f) => f.uid === uid);
    return result["form"];
  };

  /**
   * 当组件在表单内部的时候，接管 getValueFromEvent
   * 直接决定表单的值和展示出来的图片
   *
   * @param {File} {file,fileList}
   * @memberof BaseUpload
   */
  getValueFromEvent = (info) => {
    const { file, fileList } = info;
    const filterList = toArray(fileList).filter((f) => f.status !== "error");
    const full = get(this.props, "full", false);
    const uploadFormList = get(this.state, "uploadFormList", []);
    const param = { file, filterList, fileList, uploadFormList };
    const dict = this.extendParam.getValueFromHandle(param);
    if (full === true) {
      return get(dict, "completeUrls", []);
    }
    return get(dict, "urls", []);
  };

  renderChildren = (tmpProps) => {
    const { showType } = tmpProps;
    const listType = listTyleEmum[showType];
    tmpProps.change = this.onChange;
    tmpProps.getFieldDecoratorParam.getValueFromEvent = this.getValueFromEvent;
    const isRead = get(this.props, "isRead", false);
    const { spin } = this.state;
    const className = [
      "yx_upload_box",
      `${isRead ? "yx_upload_read" : ""}`,
      spin ? "yx_upload_spin" : "",
    ];
    const jsx = (
      <Upload
        listType={listType}
        accept={this.accept}
        ref={this.uploadAntd}
        fileList={this.fileList}
        action={this.extendParam.uploadUrl}
        className={className.join(" ")}
        multiple={this.props.isMultiple}
        onRemove={(file) => this.onRemove(file)}
        data={(file) => this.uploadFormData(file)}
        onPreview={(file) => this.onPreview(file)}
        beforeUpload={(file, fileList) => this.beforeUpload(file, fileList)}
        {...this.props.extraUploadParam}
      >
        {this.state.spin ? <Spin /> : renderUploadBtn(this.props, this)}
      </Upload>
    );
    return jsx;
  };

  refEvent = () => {
    const currentRef = get(this.uploadAntd, "current");
    const form = get(this.props, "form", null);
    if (currentRef != null && form != null) {
      let list = this.props.form.getFieldValue(this.props.id);
      if (!isEqual(list, this.state.fileListByForm)) {
        this.setState({ fileListByForm: list });
      }
    }
  };
  componentWillReceiveProps(nextprops) {
    this.refEvent();
  }
  componentDidMount() {
    this.refEvent();
  }
  productionHtml() {
    let tmpProps = cloneDeep(this.props, 8);
    tmpProps.rules.max = "";
    tmpProps.children = this.renderChildren(tmpProps);
    let html = Basics.html(tmpProps);
    // this.refEvent(); // 不能在渲染函数render 调用有setState函数的方法
    return html;
  }

  render() {
    const className = batchGet([this.state, this.props], "className", "");
    const style = get(this.props, "style", {});
    const node = get(this.props, "node", null);
    const showFilename = get(this.props, "showFilename", false)
      ? " yx-upload-showname"
      : "";

    return (
      <div
        className={className.join(" ") + showFilename}
        id={this.id}
        style={style}
      >
        {this.productionHtml()}
        <UploadPreview {...this.previewParam} />
        {nodeFactory(node, this.fileList)}
      </div>
    );
  }
}

export default BaseUpload;



// WEBPACK FOOTER //
// ./src/yx-widget/component/YxInput/YxUpload/BaseUpload/index.js